import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import {
  Home,
  AttachMoney,
  LocationOn,
  PhotoCamera,
  Add,
  Save
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

const AddProperty = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authUser, setAuthUser] = useState(null);
  
  useEffect(() => {
    // Get the current user from Supabase auth
    const getCurrentUser = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('Error getting user:', error);
        return;
      }
      console.log('Current user:', user);
      setAuthUser(user);
    };
    getCurrentUser();
  }, []);
  
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    propertyType: 'residential',
    status: 'for-sale',
    area: '',
    topography: 'flat',
    utilities: [],
    address: {
      city: '',
      state: '',
      country: 'Kenya',
      coordinates: { lat: null, lng: null }
    },
    features: [],
    images: []
  });
  
  const [newFeature, setNewFeature] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const propertyTypes = [
    { value: 'residential', label: 'Residential Land' },
    { value: 'agricultural', label: 'Agricultural Land' },
    { value: 'commercial', label: 'Commercial Land' },
    { value: 'industrial', label: 'Industrial Land' },
    { value: 'recreational', label: 'Recreational Land' },
    { value: 'timberland', label: 'Timberland' },
    { value: 'ranch', label: 'Ranch Land' }
  ];

  const topographyOptions = [
    { value: 'flat', label: 'Flat' },
    { value: 'gently-sloping', label: 'Gently Sloping' },
    { value: 'steep', label: 'Steep' },
    { value: 'rolling', label: 'Rolling' },
    { value: 'wooded', label: 'Wooded' },
    { value: 'wetland', label: 'Wetland' },
    { value: 'mountainous', label: 'Mountainous' }
  ];

  const utilityOptions = [
    'Electricity',
    'Water',
    'Sewer',
    'Natural Gas',
    'Phone',
    'Internet',
    'Cable TV'
  ];

  const statusOptions = [
    { value: 'for-sale', label: 'For Sale' },
    { value: 'for-rent', label: 'For Rent' },
    { value: 'sold', label: 'Sold' },
    { value: 'rented', label: 'Rented' }
  ];

  const handleInputChange = (field) => (event) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: event.target.value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value
      }));
    }
    if (error) setError('');
  };

  const addFeature = () => {
    if (newFeature.trim() && !formData.features.includes(newFeature.trim())) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (featureToRemove) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(feature => feature !== featureToRemove)
    }));
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    setLoading(true);
    setError('');
    
    try {
      console.log('Starting image upload...');
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('Please sign in to upload images');
      }
      
      console.log('User authenticated:', user.id);
      const uploadedImageUrls = [];
      
      for (const file of files) {
        console.log('Processing file:', file.name);
        
        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('File size should be less than 5MB');
        }
        
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
        
        console.log('Uploading to path:', fileName);
        
        // Upload the file
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('property-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });
          
        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }
        
        console.log('Upload successful:', uploadData);
        
        // Get the public URL
        const { data: { publicUrl } } = supabase.storage
          .from('property-images')
          .getPublicUrl(fileName);
          
        console.log('Public URL:', publicUrl);
        
        if (!publicUrl) {
          throw new Error('Failed to generate public URL');
        }
        
        uploadedImageUrls.push(publicUrl);
      }
      
      // Update form data with the new image URLs
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImageUrls]
      }));
      
      console.log('All images uploaded successfully');
      
    } catch (error) {
      console.error('Error in handleImageUpload:', error);
      setError(error.message || 'Failed to upload images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Property title is required');
      return false;
    }
    if (!formData.price || formData.price <= 0) {
      setError('Valid price is required');
      return false;
    }
    if (!formData.area || formData.area <= 0) {
      setError('Valid land area is required');
      return false;
    }
    if (!formData.address.city.trim()) {
      setError('City is required');
      return false;
    }
    if (formData.utilities.length === 0) {
      setError('Please select at least one utility');
      return false;
    }
    return true;
  };

  // Function to check the database schema
  const checkDatabaseSchema = async () => {
    try {
      // Get the column information for the properties table
      const { data: columns, error } = await supabase
        .from('information_schema.columns')
        .select('column_name, data_type, udt_name')
        .eq('table_name', 'properties');
      
      if (error) throw error;
      console.log('Properties table columns:', columns);
      return columns;
    } catch (err) {
      console.error('Error checking database schema:', err);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Validate form data
      if (!formData.title || !formData.propertyType || !formData.status || !formData.price) {
        throw new Error('Please fill in all required fields');
      }

      if (!authUser?.id) {
        throw new Error('User not authenticated');
      }

      // Convert area to number and handle decimal places
      const areaValue = formData.area ? parseFloat(formData.area) : null;
      
      // Create or get user in custom database first
      let customUserId;
      
      try {
        // First, try to find existing user by email
        const { data: existingUser, error: findError } = await supabase
          .from('users')
          .select('id')
          .eq('email', authUser.email)
          .single();
          
        if (existingUser) {
          customUserId = existingUser.id;
        } else {
          // Create new user in custom database
          const { data: newUser, error: createError } = await supabase
            .from('users')
            .insert([{
              first_name: authUser.user_metadata?.first_name || authUser.email.split('@')[0],
              last_name: authUser.user_metadata?.last_name || '',
              email: authUser.email,
              password: 'supabase_auth', // Placeholder since we use Supabase auth
              role: 'seller',
              is_verified: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }])
            .select('id')
            .single();
            
          if (createError) throw createError;
          customUserId = newUser.id;
        }
      } catch (userError) {
        console.error('Error handling user:', userError);
        throw new Error('Failed to process user information');
      }
      
      const propertyData = {
        title: formData.title,
        description: formData.description || '',
        property_type: formData.propertyType,
        status: formData.status,
        price: parseFloat(formData.price),
        area: areaValue,
        area_unit: 'acres',
        address: formData.address.street || '',
        city: formData.address.city,
        state: formData.address.state,
        zip_code: formData.address.zip || '',
        country: formData.address.country || 'Kenya',
        latitude: formData.address.coordinates?.lat || null,
        longitude: formData.address.coordinates?.lng || null,
        images: formData.images,
        features: formData.features,
        amenities: formData.utilities,
        owner_id: customUserId, // Use the integer ID from custom database
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('Property data to insert:', propertyData);

      const { data, error } = await supabase
        .from('properties')
        .insert([propertyData])
        .select();
      
      if (error) throw error;
      
      setSuccess('Property added successfully!');
      setTimeout(() => {
        navigate('/properties');
      }, 1500);
    } catch (err) {
      console.error('Error adding property:', err);
      setError(`Failed to add property: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">
          Please log in to add a property
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(128, 0, 32, 0.05) 0%, rgba(44, 44, 44, 0.05) 100%)',
        py: 4
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 1
              }}
            >
              Add New Property
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary' }}
            >
              List your property with Lewmax Agencies
            </Typography>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Property Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Land Listing Title"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  required
                  placeholder="e.g., 5-Acre Plot in Nairobi"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Home color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Property Type</InputLabel>
                  <Select
                    value={formData.propertyType}
                    label="Property Type"
                    onChange={handleInputChange('propertyType')}
                  >
                    {propertyTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleInputChange('status')}
                  >
                    {statusOptions.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange('price')}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography color="primary" sx={{ fontWeight: 'medium' }}>KSh</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Total Area (acres)"
                  type="number"
                  value={formData.area}
                  onChange={(e) => {
                    // Allow decimal values with up to 2 decimal places
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                      setFormData(prev => ({
                        ...prev,
                        area: value
                      }));
                    }
                  }}
                  inputProps={{
                    step: "0.01",
                    min: "0",
                    inputMode: "decimal"
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">acres</InputAdornment>,
                  }}
                  placeholder="0.00"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  placeholder="Describe the land, its features, and nearby amenities..."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Available Utilities
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {utilityOptions.map(utility => (
                    <Chip
                      key={utility}
                      label={utility}
                      onClick={() => {
                        const newUtilities = formData.utilities.includes(utility)
                          ? formData.utilities.filter(u => u !== utility)
                          : [...formData.utilities, utility];
                        setFormData(prev => ({ ...prev, utilities: newUtilities }));
                      }}
                      color={formData.utilities.includes(utility) ? 'primary' : 'default'}
                      variant={formData.utilities.includes(utility) ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Location */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Location
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={handleInputChange('address.city')}
                  required
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.address.state}
                  onChange={handleInputChange('address.state')}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.address.zipCode}
                  onChange={handleInputChange('address.zipCode')}
                />
              </Grid>

              {/* Features */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Features & Amenities
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Feature"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="e.g., Swimming Pool, Garage, Garden"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button
                    variant="outlined"
                    onClick={addFeature}
                    startIcon={<Add />}
                    sx={{ minWidth: 120 }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.features.map((feature, index) => (
                    <Chip
                      key={index}
                      label={feature}
                      onDelete={() => removeFeature(feature)}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>

              {/* Images */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Images
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCamera />}
                  sx={{ mb: 2 }}
                >
                  Upload Images
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
                {formData.images.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formData.images.length} image(s) selected: {formData.images.join(', ')}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={<Save />}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #800020 30%, #a0002a 90%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #600018 30%, #800020 90%)',
                      }
                    }}
                  >
                    {loading ? 'Adding Property...' : 'Add Property'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AddProperty;
